import { dateToIso, formatDataBr, isoToDate } from "@/modules/recursos/equipe-obra.rules";
import type {
  AcoesCelulaProps,
  DataCelulaProps,
  NomeCelulaProps,
  PapelCelulaProps,
  SituacaoProfissional,
} from "./types-alocacao-celulas";
import type { ProfissionalOpcao } from "../equipe-obra.types";

const PREFIXO = "equipe-obra";

export function useNomeCelula({
  indice,
  linha,
  opcoes,
  conflitosDe = () => 0,
  onChange,
}: NomeCelulaProps) {
  const selecionado = opcoes.find((opcao) => opcao.value === linha.usuarioId) ?? null;

  const gravada = Boolean(linha.gravada);

  return {
    situacaoDe: (opcao: ProfissionalOpcao): SituacaoProfissional => {
      const conflitos = conflitosDe(opcao.value);
      return {
        conflitos,
        indisponivel: opcao.indisponivel,
        disponivel: conflitos === 0 && !opcao.indisponivel,
      };
    },
    selecionado,
    // Na equipe gravada o nome vem da alocação, que pode não estar mais na listagem.
    nome: (gravada ? linha.profissionalNome : null) || selecionado?.label || "—",
    // Profissional é só leitura na equipe gravada: a linha só edita as datas.
    editavel: linha.editing && !gravada,
    erro: linha.errors.usuario,
    // Em leitura só a equipe gravada mostra o erro (a sobreposição, que o ✓ não revalida).
    erroLeitura: gravada ? linha.errors.usuario : undefined,
    testId: `${PREFIXO}-profissional-${indice}`,
    escolherProfissional: (usuarioId: string | null | undefined) =>
      onChange({ usuarioId: usuarioId ?? null }, ["usuario"]),
  };
}

export function usePapelCelula({ indice, linha, papeis, onChange }: PapelCelulaProps) {
  const selecionado = papeis.find((papel) => papel.value === linha.papelObraId)?.label ?? "";

  return {
    nome: linha.gravada ? (linha.papelNome ?? selecionado) : selecionado,
    editavel: linha.editing && !linha.gravada,
    testId: `${PREFIXO}-papel-${indice}`,
    escolherPapel: (papelObraId: string | null | undefined) =>
      onChange({ papelObraId: papelObraId ?? linha.papelObraId }, []),
  };
}

export function useDataCelula({ linha, campo, onChange }: DataCelulaProps) {
  return {
    dataFormatada: formatDataBr(linha[campo]),
    dataSelecionada: isoToDate(linha[campo]),
    alterarData: (valor: unknown) =>
      onChange({ [campo]: valor instanceof Date ? dateToIso(valor) : "" }, [campo]),
  };
}

export function useAcoesCelula({ indice, linha, nomeAcessivel }: AcoesCelulaProps) {
  return {
    // Gravada: o ✓ fica bloqueado enquanto a linha tem erro; a linha nova valida ao confirmar.
    confirmarBloqueado: Boolean(linha.gravada) && Object.values(linha.errors).some(Boolean),
    podeExcluir: !linha.gravada,
    rotuloEditar: `Editar ${nomeAcessivel}`,
    rotuloExcluir: `Excluir ${nomeAcessivel}`,
    testIds: {
      confirm: `${PREFIXO}-confirm-${indice}`,
      cancel: `${PREFIXO}-cancel-${indice}`,
      edit: `${PREFIXO}-edit-${indice}`,
      remove: `${PREFIXO}-remove-${indice}`,
    },
  };
}
